import { Box, Stack } from '@mui/material'
import React from 'react'
import { DeviceHwID, Port } from '../../types/models'
import { PortConfigRadio, RadioConfig, RadioMode, RadioPort } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { Dropdown } from '../../components/atoms/Dropdown'
import { Text } from '../../components/atoms/Text'

/**
 * Controller for radio.
 */
export class RadioPortControl extends PortControl<RadioPort, RadioConfig> {

  port: RadioPort
  oldSlaves: [string | null, string | null, string | null, string | null, string | null, string | null]

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as RadioPort
    this.oldSlaves = [null, null, null, null, null, null]
  }

  get group(): string { return this.app.t('PortGroup-Other') }

  getTypeName(): string { return this.app.t('PortType-Radio') }

  getDescription(): string {
    return this.app.t('Radio communication configuration')
  }

  getConfigDefaults(): PortConfigRadio {
    return {
      radio_mode: 'RADIO_MODE_SLAVE',
      slave_devids: [null, null, null, null, null, null]
    }
  }

  async addSlave(hwId: DeviceHwID): Promise<boolean> {
    let old: [
      DeviceHwID | null,
      DeviceHwID | null,
      DeviceHwID | null,
      DeviceHwID | null,
      DeviceHwID | null,
      DeviceHwID | null
    ] | [] = this.getConfig('slave_devids')
    if (!old || old.length === 0) {
      old = [null, null, null, null, null, null]
    }

    const idx = old.indexOf(hwId) >= 0 ? old.indexOf(hwId) : old.indexOf(null)

    if (idx < 0) {
      this.app.addError(this.app.t('Too many slave devices.'))
      return false
    } else {
      old[idx] = hwId as DeviceHwID
      this.setConfig('slave_devids', old)
      this.setConfig('radio_mode', 'RADIO_MODE_MASTER')
      await this.systemControl.flashItems([DeviceControl.buildPortFlashItem(this)])

      return this.saveDeviceValues({ slave_of: null, master_of: old.filter(hwId => hwId !== null) as DeviceHwID[] })
    }
  }

  async removeSlave(hwId: DeviceHwID): Promise<boolean> {
    const old: [
      DeviceHwID | null,
      DeviceHwID | null,
      DeviceHwID | null,
      DeviceHwID | null,
      DeviceHwID | null,
      DeviceHwID | null
    ] = this.getConfig('slave_devids') || [null, null, null, null, null, null]

    const idx = old.indexOf(hwId)
    if (idx >= 0) {
      old[idx] = null
    }

    this.setConfig('slave_devids', old)
    this.setConfig('radio_mode', 'RADIO_MODE_MASTER')
    await this.systemControl.flashItems([DeviceControl.buildPortFlashItem(this)])

    return this.saveDeviceValues({ slave_of: null, master_of: old.filter(hwId => hwId !== null) as DeviceHwID[] })
  }

  async joinMaster(hwId: DeviceHwID): Promise<boolean> {
    await this.saveDeviceValues({ slave_of: hwId, master_of: [] })
    this.setConfig('slave_devids', [])
    this.setConfig('radio_mode', 'RADIO_MODE_SLAVE')
    await this.systemControl.flashItems([DeviceControl.buildPortFlashItem(this)])
    return true
  }

  async leaveMaster(): Promise<boolean> {
    await this.saveDeviceValues({ slave_of: null, master_of: [] })
    this.setConfig('slave_devids', [])
    this.setConfig('radio_mode', 'RADIO_MODE_SLAVE')
    await this.systemControl.flashItems([DeviceControl.buildPortFlashItem(this)])
    return true
  }

  configTopLeft(): React.ReactNode {
    const onChange = (mode: RadioMode) => {
      this.setConfig('radio_mode', mode)
      if (mode === 'RADIO_MODE_DISABLED' || mode === 'RADIO_MODE_SLAVE') {
        this.oldSlaves = this.getConfig('slave_devids')
        this.setConfig('slave_devids', [])
      } else {
        this.setConfig('slave_devids', this.oldSlaves)
      }
    }

    return (
      <Box sx={{ my: 2 }}>
        <Dropdown
          label={this.app.t('Radio Mode')}
          items={{
            RADIO_MODE_SLAVE: this.app.t('RadioMode-RADIO_MODE_SLAVE'),
            RADIO_MODE_MASTER: this.app.t('RadioMode-RADIO_MODE_MASTER'),
            RADIO_MODE_DISABLED: this.app.t('RadioMode-RADIO_MODE_DISABLED')
          }}
          selected={
            this.getConfig('radio_mode')
          }
          onSelect={
            value => onChange(value as RadioMode)
          }
        />
      </Box>
    )
  }

  configTopRight(): React.ReactNode {
    const mode = this.getConfig('radio_mode')
    const slaveIds: [string | null, string | null, string | null, string | null, string | null] = this.getConfig('slave_devids') || [null, null, null, null, null, null]

    const onChange = (idx: number, value: string) => {
      slaveIds[idx] = value === '' ? null : value
      this.setConfig('slave_devids', slaveIds)
    }

    return (
      <Box sx={{ my: 2 }}>
        {
            mode === 'RADIO_MODE_MASTER' &&
            <Stack>
              {[0, 1, 2, 3, 4, 5].map(n =>
                <Text
                  key={n}
                  margin="normal"
                  label={this.app.t('Slave {number} HW ID').replace('{number}', '' + (n + 1))}
                  sx={{ width: '70%' }}
                  size="small"
                  value={slaveIds[n] || ''}
                  type="text"
                  onChange={(e) => onChange(n, e.target.value.toUpperCase())}
                />
              )}
            </Stack>
        }
      </Box>
    )
  }
}
